import * as XLSX from "xlsx"
import type {
  CombinedOrderReport,
  MeeshoCombinedReport,
  SkuCostConfig,
  SkuReportItem
} from "@/features/profit-calculators/calculator-shared/types"
import type { AmazonOrderRow, AmazonTransactionRow } from "@/features/profit-calculators/amazon-calculator/types"

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0
  const number = parseFloat(String(value).replace(/[^\d.-]/g, ""))
  return Number.isNaN(number) ? 0 : number
}

const toString = (value: unknown): string => (value === null || value === undefined ? "" : String(value).trim())

export async function parseAmazonAllOrders(file: File): Promise<{
  orders: AmazonOrderRow[]
  uniqueSkus: { sku: string; productName: string }[]
}> {
  const text = await file.text()
  const wb = XLSX.read(text, { type: "string" })
  const ws = wb.Sheets[wb.SheetNames[0]]
  if (!ws) throw new Error("Orders report file is empty.")

  const rows = XLSX.utils.sheet_to_json<any>(ws)
  const orders: AmazonOrderRow[] = []
  const skusMap = new Map<string, string>()

  for (const r of rows) {
    const orderId = toString(r["amazon-order-id"])
    const sku = toString(r["sku"])
    const prodName = toString(r["product-name"])
    const qty = toNumber(r["quantity"])
    const status = toString(r["order-status"])
    const purchaseDate = toString(r["purchase-date"])

    if (!sku) continue

    orders.push({
      amazonOrderId: orderId,
      sku,
      productName: prodName,
      quantity: qty,
      orderStatus: status,
      purchaseDate,
      itemPrice: toNumber(r["item-price"])
    })

    if (sku && prodName && !skusMap.has(sku)) {
      skusMap.set(sku, prodName)
    }
  }

  const uniqueSkus = Array.from(skusMap.entries()).map(([sku, productName]) => ({
    sku,
    productName
  }))

  return { orders, uniqueSkus }
}

export async function parseAmazonTransactions(file: File): Promise<AmazonTransactionRow[]> {
  const text = await file.text()
  const lines = text.split(/\r?\n/)
  const headerIdx = lines.findIndex(line => line.startsWith('"date/time"') || line.startsWith("date/time"))
  if (headerIdx === -1) {
    throw new Error("Could not find Amazon Transaction CSV headers starting with 'date/time'.")
  }
  const cleanCsv = lines.slice(headerIdx).join("\n")
  const wb = XLSX.read(cleanCsv, { type: "string" })
  const ws = wb.Sheets[wb.SheetNames[0]]
  if (!ws) throw new Error("Transaction CSV is empty.")

  const rows = XLSX.utils.sheet_to_json<any>(ws)
  const transactions: AmazonTransactionRow[] = []

  for (const r of rows) {
    const dateTime = toString(r["date/time"])
    const type = toString(r["type"])
    if (!dateTime || !type) continue

    transactions.push({
      dateTime,
      settlementId: toString(r["settlement id"]),
      type,
      orderId: toString(r["order id"]),
      sku: toString(r["Sku"] || r["sku"] || r["SKU"]),
      description: toString(r["description"]),
      quantity: toNumber(r["quantity"]),
      productSales: toNumber(r["product sales"]),
      shippingCredits: toNumber(r["shipping credits"]),
      giftWrapCredits: toNumber(r["gift wrap credits"]),
      promotionalRebates: toNumber(r["promotional rebates"]),
      totalSalesTaxLiable: toNumber(r["Total sales tax liable(GST before adjusting TCS)"]),
      tcsCgst: toNumber(r["TCS-CGST"]),
      tcsSgst: toNumber(r["TCS-SGST"]),
      tcsIgst: toNumber(r["TCS-IGST"]),
      tds: toNumber(r["TDS (Section 194-O)"]),
      sellingFees: toNumber(r["selling fees"]),
      fbaFees: toNumber(r["fba fees"]),
      otherTransactionFees: toNumber(r["other transaction fees"]),
      other: toNumber(r["other"]),
      total: toNumber(r["total"]),
      status: toString(r["Transaction Status"] || r["status"] || "Released")
    })
  }

  return transactions
}

// Helper to extract month label
export function getAmazonMonthLabel(dateStr: string): string {
  if (!dateStr) return "Unknown Month"
  const parts = dateStr.trim().split(/\s+/)
  if (parts.length >= 3) {
    const month = parts[1] // e.g. "May"
    const year = parts[2] // e.g. "2026"
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
      "January",
      "February",
      "March",
      "April",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ]
    const monthLower = month.toLowerCase()
    const matchedMonth = months.find(m => m.toLowerCase().startsWith(monthLower.slice(0, 3)))
    if (matchedMonth && /^\d{4}$/.test(year)) {
      return `${matchedMonth.slice(0, 3)} ${year}`
    }
  }

  const date = new Date(dateStr)
  if (!isNaN(date.getTime())) {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return `${months[date.getMonth()]} ${date.getFullYear()}`
  }

  return "Unknown Month"
}

export function computeAmazonCombinedReport(
  orders: AmazonOrderRow[],
  transactions: AmazonTransactionRow[],
  skuCosts: Record<string, SkuCostConfig>
): Record<string, MeeshoCombinedReport> {
  const orderIdToSku: Record<string, string> = {}
  const skuToName: Record<string, string> = {}

  for (const o of orders) {
    if (o.amazonOrderId && o.sku) {
      orderIdToSku[o.amazonOrderId] = o.sku
    }
    if (o.sku && o.productName) {
      skuToName[o.sku] = o.productName
    }
  }

  // Get month label from ISO purchase date (UTC)
  const getOrderMonthLabel = (purchaseDate: string): string => {
    if (!purchaseDate) return ""
    const date = new Date(purchaseDate)
    if (isNaN(date.getTime())) return ""
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return `${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`
  }

  // 1. Derive unique months from TSV orders (purchase dates), not transaction dates
  const monthsSet = new Set<string>()
  for (const o of orders) {
    if (o.orderStatus === "Cancelled") continue
    const label = getOrderMonthLabel(o.purchaseDate)
    if (label) monthsSet.add(label)
  }

  // 2. Index all released transactions by orderId for fast lookup
  const txByOrderId: Record<string, AmazonTransactionRow[]> = {}
  const nonOrderTx: AmazonTransactionRow[] = []

  for (const t of transactions) {
    if (t.type === "Transfer" || t.type === "Debt") continue
    if (t.orderId) {
      if (!txByOrderId[t.orderId]) txByOrderId[t.orderId] = []
      txByOrderId[t.orderId].push(t)
    } else {
      nonOrderTx.push(t)
    }
  }

  const reports: Record<string, MeeshoCombinedReport> = {}

  for (const monthLabel of monthsSet) {
    // A. TSV orders for this month
    const monthTsvOrders = orders.filter(o => getOrderMonthLabel(o.purchaseDate) === monthLabel)
    const monthOrderIds = new Set(monthTsvOrders.map(o => o.amazonOrderId).filter(Boolean))

    // B. Collect ALL released transactions for this month's orders (by orderId regardless of tx date)
    const orderReleasedTx: AmazonTransactionRow[] = []
    for (const orderId of monthOrderIds) {
      for (const t of txByOrderId[orderId] || []) {
        if (t.status === "Released") orderReleasedTx.push(t)
      }
    }

    // C. Non-order released transactions for this month (ads, reimbursements, etc.) by tx date
    const nonOrderReleasedTx = nonOrderTx.filter(
      t => t.status === "Released" && getAmazonMonthLabel(t.dateTime) === monthLabel
    )

    // D. Full released tx set for this month
    const releasedTx = [...orderReleasedTx, ...nonOrderReleasedTx]

    // E. Refunded order IDs
    const refundedOrders = new Set<string>()
    for (const t of releasedTx) {
      if (t.type === "Refund" && t.orderId) refundedOrders.add(t.orderId)
    }

    // SUMMARY FINANCIALS

    // Sales (Inc GST): product sales + shipping credits + gift wrap - promo rebates + GST
    // from Order-type released rows only
    let salesIncGst = 0
    for (const t of releasedTx) {
      if (t.type === "Order") {
        salesIncGst +=
          t.productSales +
          t.shippingCredits +
          t.giftWrapCredits -
          Math.abs(t.promotionalRebates) +
          t.totalSalesTaxLiable
      }
    }

    // Advertising: Service Fee rows where description mentions "advertising"
    let totalAdsSpend = 0
    for (const t of releasedTx) {
      if (t.type === "Service Fee" && t.description.toLowerCase().includes("advertising")) {
        totalAdsSpend += Math.abs(t.total)
      }
    }

    // Net Settlement: sum of total for ALL released rows except advertising Service Fees
    let netSettlement = 0
    for (const t of releasedTx) {
      if (t.type === "Service Fee" && t.description.toLowerCase().includes("advertising")) continue
      netSettlement += t.total
    }

    const refundsFees = salesIncGst - netSettlement

    // ORDER COUNTS
    let overallOrdersCount = 0
    let deliveredNotReturnedCount = 0
    let deliveredReturnedCount = 0
    let undeliveredCourierReturnCount = 0
    let easyShipOrdersCount = 0

    for (const r of monthTsvOrders) {
      if (r.orderStatus === "Cancelled") continue
      overallOrdersCount++
      const orderId = r.amazonOrderId || ""
      if (r.orderStatus === "Shipped - Returned to Seller") {
        undeliveredCourierReturnCount++
      } else {
        if (refundedOrders.has(orderId)) {
          deliveredReturnedCount++
        } else {
          deliveredNotReturnedCount++
          if (r.orderStatus === "Shipped - Delivered to Buyer") easyShipOrdersCount++
        }
      }
    }

    // COGS: delivered = product+packing (inc GST), returns = packing only (inc GST)
    let totalCogs = 0
    for (const r of monthTsvOrders) {
      if (r.orderStatus === "Cancelled") continue
      const sku = r.sku || ""
      const costConfig = skuCosts[sku]
      if (!costConfig) continue
      const productCostWithGst = costConfig.productPrice * (1 + costConfig.productGst / 100)
      const packingCostWithGst = costConfig.packingPrice * (1 + costConfig.packingGst / 100)
      const qty = r.quantity || 1
      const orderId = r.amazonOrderId || ""
      const isReturn = r.orderStatus === "Shipped - Returned to Seller" || refundedOrders.has(orderId)
      totalCogs += isReturn ? packingCostWithGst * qty : (productCostWithGst + packingCostWithGst) * qty
    }

    const profit = netSettlement - totalAdsSpend - totalCogs

    // SKU-LEVEL REPORTS
    const skuMap: Record<string, SkuReportItem> = {}

    for (const r of monthTsvOrders) {
      if (r.orderStatus === "Cancelled") continue
      const sku = (r.sku || "UNMAPPED").trim()
      const prodName = r.productName || skuToName[sku] || "Unmapped SKU"
      if (!skuMap[sku]) {
        skuMap[sku] = {
          sku,
          productName: prodName,
          settlement: 0,
          purchaseCost: 0,
          unitsSold: 0,
          unitsReturned: 0,
          profit: 0,
          category: "LOW"
        }
      }
      const item = skuMap[sku]
      const qty = r.quantity || 1
      const orderId = r.amazonOrderId || ""
      const costConfig = skuCosts[sku]
      const isReturn = r.orderStatus === "Shipped - Returned to Seller" || refundedOrders.has(orderId)
      if (costConfig) {
        const productCostWithGst = costConfig.productPrice * (1 + costConfig.productGst / 100)
        const packingCostWithGst = costConfig.packingPrice * (1 + costConfig.packingGst / 100)
        item.purchaseCost += isReturn ? packingCostWithGst * qty : (productCostWithGst + packingCostWithGst) * qty
      }
      if (isReturn) {
        item.unitsReturned += qty
      } else {
        item.unitsSold += qty
      }
    }

    // Attribute settlement to SKUs via orderId mapping
    for (const t of releasedTx) {
      if (t.type === "Service Fee" && t.description.toLowerCase().includes("advertising")) continue
      const resolvedSku = (t.sku && t.sku.trim()) || (t.orderId ? orderIdToSku[t.orderId] : "") || ""
      if (resolvedSku && skuMap[resolvedSku]) {
        skuMap[resolvedSku].settlement += t.total
      }
    }

    const skuReportsList: SkuReportItem[] = []
    for (const sku in skuMap) {
      const item = skuMap[sku]
      item.profit = item.settlement - item.purchaseCost
      let category: "HIGH" | "MEDIUM" | "LOW" | "LOSS" = "LOW"
      if (item.profit <= 0) category = "LOSS"
      else if (item.profit > 2000) category = "HIGH"
      else if (item.profit >= 500) category = "MEDIUM"
      item.category = category
      skuReportsList.push(item)
    }
    skuReportsList.sort((a, b) => b.profit - a.profit)

    // ORDER-LEVEL DETAILS
    const orderDetailsMap: Record<string, any> = {}
    for (const r of monthTsvOrders) {
      if (r.orderStatus === "Cancelled") continue
      const orderId = r.amazonOrderId || ""
      const sku = r.sku || ""
      const prodName = r.productName || skuToName[sku] || "Unmapped Order"
      const costConfig = skuCosts[sku]
      const qty = r.quantity || 1
      const isReturn = r.orderStatus === "Shipped - Returned to Seller" || refundedOrders.has(orderId)
      let itemCogs = 0
      if (costConfig) {
        const productCostWithGst = costConfig.productPrice * (1 + costConfig.productGst / 100)
        const packingCostWithGst = costConfig.packingPrice * (1 + costConfig.packingGst / 100)
        itemCogs = isReturn ? packingCostWithGst * qty : (productCostWithGst + packingCostWithGst) * qty
      }
      orderDetailsMap[orderId] = {
        orderId,
        orderDate: r.purchaseDate || "",
        productName: prodName,
        sku,
        liveOrderStatus: isReturn ? "RETURNED" : "DELIVERED",
        quantity: qty,
        settlementAmount: 0,
        cogs: itemCogs,
        originalRows: []
      }
    }

    for (const t of releasedTx) {
      if (t.type === "Service Fee" && t.description.toLowerCase().includes("advertising")) continue
      const orderId = t.orderId
      if (orderId && orderDetailsMap[orderId]) {
        orderDetailsMap[orderId].originalRows.push(t)
        orderDetailsMap[orderId].settlementAmount += t.total
      }
    }

    const ordersList: CombinedOrderReport[] = Object.values(orderDetailsMap).map(
      (o: any) =>
        ({
          subOrderNo: o.orderId,
          orderDate: o.orderDate,
          productName: o.productName,
          sku: o.sku,
          liveOrderStatus: o.liveOrderStatus,
          quantity: o.quantity,
          settlementAmount: o.settlementAmount,
          cogs: o.cogs,
          profit: o.settlementAmount - o.cogs,
          originalRows: o.originalRows
        }) as any
    )

    const reportId = `AZ-${Math.floor(1000 + Math.random() * 9000)}`

    reports[monthLabel] = {
      periodLabel: monthLabel,
      reportId,
      totalOrders: overallOrdersCount,
      totalUnits: overallOrdersCount,
      totalSettlement: netSettlement,
      totalPurchaseCost: totalCogs,
      totalAdsCost: totalAdsSpend,
      totalReferral: 0,
      netProfit: profit,
      deliveredSplit: {
        notReturned: {
          count: deliveredNotReturnedCount,
          units: deliveredNotReturnedCount,
          settlement: 0
        },
        returned: {
          count: deliveredReturnedCount,
          units: deliveredReturnedCount,
          settlement: 0
        }
      },
      undeliveredSplit: {
        cancelled: {
          count: monthTsvOrders.filter(o => o.orderStatus === "Cancelled").length,
          units: 0,
          settlement: 0
        },
        claimed: { count: 0, units: 0, settlement: 0 },
        rtoComplete: {
          count: undeliveredCourierReturnCount,
          units: undeliveredCourierReturnCount,
          settlement: 0
        },
        exchanged: { count: 0, units: 0, settlement: 0 },
        rtoLocked: { count: 0, units: 0, settlement: 0 }
      },
      skuReports: skuReportsList,
      orders: ordersList,
      files: []
    }

    const expando = reports[monthLabel] as any
    expando.amazonMetadata = {
      salesIncGst,
      refundsFees,
      netSettlement,
      advertising: totalAdsSpend,
      cogs: totalCogs,
      netProfit: profit,
      easyShipOrders: easyShipOrdersCount
    }
  }

  return reports
}
